/**
 * №53 (53.2) — the job description, as `docs/53-mockups/workforce-2.png` draws it.
 *
 * > *"Read it the way you would for a new clerk. The right-hand column is the most
 * > important copy in the product — and every line of it names the wall that
 * > actually enforces it, so the list cannot drift from what the system will do."*
 *
 * All the judgement is in `job-description.ts`, which is pure and gated there.
 * This file renders it, and its one editorial rule is that **a restraint and its
 * wall are never separable**: they are one `<li>`, so no future tidy-up can keep
 * the reassuring half and drop the checkable one.
 */
import { useAreas, useRoles, useRoleAssignments } from "../../hooks/use-policy";
import { deriveCapabilities } from "./staff-desk";
import { deriveWillNeverDo, willDoLines } from "./job-description";

export interface JobDescriptionProps {
  agentId: string;
  name: string;
  /** `AgentConfig.will_do` — declared, never derived. See the model's header. */
  willDo?: string[] | null;
  /** `AgentConfig.autonomy`. */
  autonomy?: string | null;
  /** Equipment this hire holds, with what it may do — D-CR12's read/write badges. */
  equipment?: { label: string; access?: string | null; equipped?: boolean }[];
  reportsTo?: string | null;
  firstMatters?: string[];
  onClose: () => void;
}

export function JobDescriptionModal(props: JobDescriptionProps) {
  const areas = useAreas();
  const roles = useRoles();
  const assignments = useRoleAssignments();

  const caps = deriveCapabilities(props.agentId, assignments.data ?? [], roles.data ?? [], areas.data);
  const granted = new Set(caps.can.map((a) => a.key));
  const never = deriveWillNeverDo({
    areas: areas.data?.areas ?? [],
    granted,
    autonomy: props.autonomy ?? null,
  });
  const will = willDoLines(props.willDo);

  return (
    <div className="jdscrim" role="dialog" aria-modal="true" aria-label={`Job description — ${props.name}`} data-testid="job-description" onClick={props.onClose}>
      <div className="jdbox" onClick={(e) => e.stopPropagation()}>
        <h3>Hire: {props.name}</h3>
        <p className="sub">A role, not a feature. It starts the moment you hire it, and it will introduce itself by reading its matters.</p>

        <div className="jd">
          <div className="willdo">
            <h5>Will do, on its own</h5>
            {will.length > 0 ? (
              <ul data-testid="jd-will-do">{will.map((l) => <li key={l}>{l}</li>)}</ul>
            ) : (
              /* D-CR2 — nothing declared means nothing rendered, said out loud.
                 An invented list here would read exactly like a real one. */
              <p className="jdempty" data-testid="jd-will-do-empty">
                Nothing declared yet. What a hire is FOR is authored, not inferred — the walls
                below know what it may not touch, not what it is for.
              </p>
            )}

            <h5>Equipment — and what it may do with each</h5>
            <div className="eqp" data-testid="jd-equipment">
              {(props.equipment ?? []).length === 0 && <span>nothing equipped</span>}
              {(props.equipment ?? []).map((e) => (
                <span key={e.label} className={e.equipped === false ? undefined : "on"}>
                  {e.label}<b>{e.equipped === false ? "not equipped" : (e.access ?? "read")}</b>
                </span>
              ))}
            </div>
          </div>

          <div className="never">
            <h5>Will never do without a card</h5>
            {/* The restraint and its wall are ONE list item, on purpose. */}
            <ul data-testid="jd-will-never">
              {never.map((r) => (
                <li key={r.wall}>{r.text}<span className="wall">{r.wall}</span></li>
              ))}
            </ul>

            {props.reportsTo && (<><h5>Reports to</h5><p style={{ margin: "0 0 12px" }}>{props.reportsTo}</p></>)}
            {(props.firstMatters ?? []).length > 0 && (
              <><h5>First matters</h5><p style={{ margin: 0 }}>{props.firstMatters!.join(" · ")}</p></>
            )}
          </div>
        </div>

        <div className="jdrow">
          <button type="button" onClick={props.onClose} data-testid="jd-close">Close</button>
        </div>
      </div>
    </div>
  );
}
